import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Send,
  Loader2,
  Check,
  MessageSquare,
  Clock,
  CheckCircle2,
  StickyNote,
  Zap,
  User,
  ChevronDown,
  UserPlus,
  Phone,
  MapPin,
  Plane,
  Hotel,
  Car,
  UtensilsCrossed,
  MoreHorizontal,
  Users,
  Pin,
  PinOff,
  Star,
  ChevronUp,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUpload } from "@/components/chat/FileUpload";
import { MessageAttachment } from "@/components/chat/MessageAttachment";
import { TruncatedMessage } from "@/components/chat/TruncatedMessage";
import { ScrollToBottomButton } from "@/components/chat/ScrollToBottomButton";
import { MessageStatus } from "@/components/chat/MessageStatus";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { OnlineStatus } from "@/components/chat/OnlineStatus";
import { QuickRepliesSheet } from "@/components/chat/QuickRepliesSheet";
import { useTypingIndicator } from "@/hooks/useTypingIndicator";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import ReassignDialog from "./ReassignDialog";

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_from_agent: boolean;
  is_read: boolean;
  read_at: string | null;
  delivered_at: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  created_at: string;
  is_pinned: boolean;
  pinned_at: string | null;
  pinned_by: string | null;
  is_starred: boolean;
  starred_at: string | null;
  starred_by: string | null;
}

interface InternalNote {
  id: string;
  conversation_id: string;
  agent_id: string;
  content: string;
  created_at: string;
}

interface QuickReply {
  id: string;
  title: string;
  content: string;
  category: string | null;
}

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  address_line1: string | null;
  city: string | null;
  emirate: string | null;
  country: string | null;
}

interface Conversation {
  id: string;
  user_id: string;
  assigned_agent_id: string | null;
  status: "open" | "pending" | "closed";
  subject: string | null;
  service: string | null;
  user_profile?: UserProfile;
}

interface AgentChatViewProps {
  conversation: Conversation;
  onStatusChange: (status: "open" | "pending" | "closed") => void;
  onTakeOver?: () => void;
  onBack?: () => void;
  isMobile?: boolean;
}

const getServiceIcon = (service: string | null) => {
  switch (service) {
    case "flights": return <Plane size={14} />;
    case "hotels": return <Hotel size={14} />;
    case "car_rental": return <Car size={14} />;
    case "restaurants": return <UtensilsCrossed size={14} />;
    default: return <MoreHorizontal size={14} />;
  }
};

export default function AgentChatView({
  conversation,
  onStatusChange,
  onTakeOver,
  onBack,
  isMobile = false,
}: AgentChatViewProps) {
  const { user, isAdmin } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [notes, setNotes] = useState<InternalNote[]>([]);
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [fullProfile, setFullProfile] = useState<UserProfile | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [showStarredPanel, setShowStarredPanel] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Typing indicator hook
  const { typingUsers, sendTyping, stopTyping, isTyping } = useTypingIndicator(conversation.id);
  
  // Online status hook
  const clientOnlineStatus = useOnlineStatus(conversation.user_id);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const scrollToMessage = useCallback((messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      // Highlight animation
      messageElement.classList.add("ring-2", "ring-primary", "ring-offset-2");
      setTimeout(() => {
        messageElement.classList.remove("ring-2", "ring-primary", "ring-offset-2");
      }, 2000);
    }
  }, []);

  // Handle scroll to show/hide scroll button
  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollButton(distanceFromBottom > window.innerHeight);
    }
  }, []);

  // Fetch messages, notes, quick replies, and full profile
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Fetch messages
      const { data: msgData } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversation.id)
        .order("created_at", { ascending: true });

      if (msgData) setMessages(msgData as Message[]);

      // Fetch internal notes
      const { data: notesData } = await supabase
        .from("internal_notes")
        .select("*")
        .eq("conversation_id", conversation.id)
        .order("created_at", { ascending: false });

      if (notesData) setNotes(notesData as InternalNote[]);

      // Fetch quick replies
      const { data: repliesData } = await supabase
        .from("quick_replies")
        .select("*")
        .order("title", { ascending: true });

      if (repliesData) setQuickReplies(repliesData as QuickReply[]);

      // Fetch full profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("first_name, last_name, phone, address_line1, city, emirate, country")
        .eq("id", conversation.user_id)
        .single();

      if (profileData) setFullProfile(profileData);

      // Mark messages as read
      const { error: updateError } = await supabase
        .from("messages")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("conversation_id", conversation.id)
        .eq("is_from_agent", false)
        .eq("is_read", false);

      if (!updateError) {
        // Reset unread count - this will trigger the realtime update
        await supabase
          .from("conversations")
          .update({ unread_count_agent: 0 })
          .eq("id", conversation.id);
      }

      setLoading(false);
    };

    fetchData();
  }, [conversation.id, conversation.user_id]);

  // Scroll to bottom after initial load
  useEffect(() => {
    if (!loading && messages.length > 0) {
      setTimeout(() => scrollToBottom("instant"), 100);
    }
  }, [loading, scrollToBottom]);

  // Real-time subscription for messages
  useEffect(() => {
    const channel = supabase
      .channel(`agent-messages-${conversation.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        async (payload) => {
          const newMsg = payload.new as Message;
          setMessages((prev) => {
            if (prev.find((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          
          // Mark as read if from user and we're viewing
          if (!newMsg.is_from_agent) {
            await supabase
              .from("messages")
              .update({ is_read: true, read_at: new Date().toISOString() })
              .eq("id", newMsg.id);
            
            // Also reset unread count
            await supabase
              .from("conversations")
              .update({ unread_count_agent: 0 })
              .eq("id", conversation.id);
          }
          
          scrollToBottom();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        (payload) => {
          // Update message read status in real-time
          const updatedMsg = payload.new as Message;
          setMessages((prev) =>
            prev.map((m) => (m.id === updatedMsg.id ? updatedMsg : m))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation.id, scrollToBottom]);

  const handleSend = async () => {
    if (!newMessage.trim() || !user || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage("");

    const { error } = await supabase.from("messages").insert({
      conversation_id: conversation.id,
      sender_id: user.id,
      content: messageContent,
      is_from_agent: true,
      delivered_at: new Date().toISOString(),
    });

    if (error) {
      toast.error("Failed to send message");
      setNewMessage(messageContent);
    } else {
      // Auto-assign if not assigned and status is pending
      if (!conversation.assigned_agent_id) {
        onTakeOver?.();
      }
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    }

    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !user || savingNote) return;

    setSavingNote(true);
    const { error } = await supabase.from("internal_notes").insert({
      conversation_id: conversation.id,
      agent_id: user.id,
      content: newNote.trim(),
    });

    if (error) {
      toast.error("Failed to add note");
    } else {
      toast.success("Note added");
      setNewNote("");
      const { data } = await supabase
        .from("internal_notes")
        .select("*")
        .eq("conversation_id", conversation.id)
        .order("created_at", { ascending: false });
      if (data) setNotes(data as InternalNote[]);
    }

    setSavingNote(false);
  };

  // Status change - Admin only
  const handleStatusChange = async (status: "open" | "pending" | "closed") => {
    if (!isAdmin) {
      toast.error("Seuls les administrateurs peuvent modifier le statut");
      return;
    }
    
    const { error } = await supabase
      .from("conversations")
      .update({ status })
      .eq("id", conversation.id);

    if (error) {
      toast.error("Échec de la mise à jour du statut");
    } else {
      toast.success(`Statut modifié: ${status}`);
      onStatusChange(status);
    }
  };

  // Service change - Admin only
  const handleServiceChange = async (service: string) => {
    if (!isAdmin) {
      toast.error("Seuls les administrateurs peuvent modifier le service");
      return;
    }
    
    const { error } = await supabase
      .from("conversations")
      .update({ service })
      .eq("id", conversation.id);

    if (error) {
      toast.error("Échec de la mise à jour du service");
    } else {
      toast.success(`Service modifié: ${service}`);
    }
  };

  const handleQuickReply = (content: string) => {
    setNewMessage(content);
    inputRef.current?.focus();
  };

  const handleTogglePin = async (messageId: string, currentlyPinned: boolean) => {
    const { error } = await supabase
      .from("messages")
      .update({
        is_pinned: !currentlyPinned,
        pinned_at: !currentlyPinned ? new Date().toISOString() : null,
        pinned_by: !currentlyPinned ? user?.id : null,
      })
      .eq("id", messageId);

    if (error) {
      toast.error("Erreur lors de l'épinglage");
    } else {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, is_pinned: !currentlyPinned, pinned_at: !currentlyPinned ? new Date().toISOString() : null, pinned_by: !currentlyPinned ? user?.id || null : null }
            : m
        )
      );
      toast.success(currentlyPinned ? "Message désépinglé" : "Message épinglé");
    }
  };

  const handleToggleStar = async (messageId: string, currentlyStarred: boolean) => {
    const { error } = await supabase
      .from("messages")
      .update({
        is_starred: !currentlyStarred,
        starred_at: !currentlyStarred ? new Date().toISOString() : null,
        starred_by: !currentlyStarred ? user?.id : null,
      })
      .eq("id", messageId);

    if (error) {
      toast.error("Erreur");
    } else {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, is_starred: !currentlyStarred, starred_at: !currentlyStarred ? new Date().toISOString() : null, starred_by: !currentlyStarred ? user?.id || null : null }
            : m
        )
      );
    }
  };

  const formatTime = (dateStr: string) => format(new Date(dateStr), "HH:mm");

  const pinnedMessages = messages.filter(m => m.is_pinned);
  const starredMessages = messages.filter(m => m.is_starred);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const profile = fullProfile || conversation.user_profile;
  const customerName = `${profile?.first_name || "Unknown"} ${profile?.last_name || "User"}`;
  const isAssignedToMe = conversation.assigned_agent_id === user?.id;
  const isArchived = (conversation as any).is_archived;

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header - iOS Style */}
      <header 
        className="border-b border-border/50 bg-background/80 backdrop-blur-xl sticky top-0 z-10"
        style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
      >
        {/* Main Header Row */}
        <div className="px-4 py-3 flex items-center gap-3">
          {/* Back button for mobile */}
          {isMobile && onBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full shrink-0"
              onClick={onBack}
            >
              <ArrowLeft size={18} />
            </Button>
          )}

          {/* Avatar & Name */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <span className="text-primary font-semibold text-sm">
                  {profile?.first_name?.charAt(0) || "?"}{profile?.last_name?.charAt(0) || ""}
                </span>
              </div>
              {/* Online indicator */}
              <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${
                conversation.status === "open" ? "bg-emerald-500" : 
                conversation.status === "pending" ? "bg-amber-500" : "bg-muted-foreground/50"
              }`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h2 className="font-medium text-foreground text-[15px] truncate leading-tight">
                {customerName}
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                <OnlineStatus 
                  isOnline={clientOnlineStatus.isOnline} 
                  lastSeenAt={clientOnlineStatus.lastSeenAt}
                  showDot={false}
                />
                {!isMobile && (
                  <>
                    <span className="text-muted-foreground/40">•</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      {getServiceIcon(conversation.service)}
                      {conversation.service?.replace('_', ' ') || "Autre"}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - Compact */}
          <div className="flex items-center gap-1">
            {/* Take Over */}
            {!conversation.assigned_agent_id && !isArchived && (
              <Button 
                variant="default" 
                size="sm" 
                className="h-8 text-xs gap-1.5 rounded-full px-3"
                onClick={onTakeOver}
              >
                <UserPlus size={14} />
                {!isMobile && "Prendre"}
              </Button>
            )}

            {/* Reassign - Admin only */}
            {isAdmin && conversation.assigned_agent_id && !isArchived && (
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => setReassignOpen(true)}
                title="Transférer"
              >
                <Users size={16} />
              </Button>
            )}

            {/* Phone */}
            {profile?.phone && (
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 rounded-full"
                title={profile.phone}
                onClick={() => window.open(`tel:${profile.phone}`)}
              >
                <Phone size={16} />
              </Button>
            )}

            {/* Notes */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="Notes">
                  <StickyNote size={16} />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle className="font-serif">Notes internes</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Ajouter une note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={3}
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNote.trim() || savingNote}
                      size="sm"
                      className="w-full"
                    >
                      {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div
                        key={note.id}
                        className="p-3 bg-muted/50 rounded-xl"
                      >
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {note.content}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-2">
                          {format(new Date(note.created_at), "d MMM, HH:mm")}
                        </p>
                      </div>
                    ))}
                    {notes.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Aucune note
                      </p>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Quick Replies - Desktop dropdown, Mobile sheet */}
            {isMobile ? (
              <QuickRepliesSheet onSelect={handleQuickReply} />
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="Réponses rapides">
                    <Zap size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    Réponses rapides
                  </div>
                  <DropdownMenuSeparator />
                  {quickReplies.map((reply) => (
                    <DropdownMenuItem
                      key={reply.id}
                      onClick={() => handleQuickReply(reply.content)}
                    >
                      <span className="truncate">{reply.title}</span>
                    </DropdownMenuItem>
                  ))}
                  {quickReplies.length === 0 && (
                    <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                      Aucune réponse configurée
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* More Options - Admin */}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    Statut
                  </div>
                  <DropdownMenuItem onClick={() => handleStatusChange("open")} className="gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    Ouvert
                    {conversation.status === "open" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange("pending")} className="gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    En attente
                    {conversation.status === "pending" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange("closed")} className="gap-2">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/50" />
                    Fermé
                    {conversation.status === "closed" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    Service
                  </div>
                  <DropdownMenuItem onClick={() => handleServiceChange("flights")} className="gap-2">
                    <Plane size={14} />
                    Vols
                    {conversation.service === "flights" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleServiceChange("hotels")} className="gap-2">
                    <Hotel size={14} />
                    Hôtels
                    {conversation.service === "hotels" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleServiceChange("car_rental")} className="gap-2">
                    <Car size={14} />
                    Voiture
                    {conversation.service === "car_rental" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleServiceChange("restaurants")} className="gap-2">
                    <UtensilsCrossed size={14} />
                    Restaurants
                    {conversation.service === "restaurants" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleServiceChange("other")} className="gap-2">
                    <MoreHorizontal size={14} />
                    Autre
                    {conversation.service === "other" && <Check size={14} className="ml-auto" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Status Bar - Subtle */}
        <div className="px-4 pb-2 flex items-center gap-2 flex-wrap">
          <div className={`
            inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full
            ${conversation.status === "open" ? "bg-emerald-500/10 text-emerald-600" : 
              conversation.status === "pending" ? "bg-amber-500/10 text-amber-600" : 
              "bg-muted text-muted-foreground"}
          `}>
            {conversation.status === "open" && <MessageSquare size={10} />}
            {conversation.status === "pending" && <Clock size={10} />}
            {conversation.status === "closed" && <CheckCircle2 size={10} />}
            {conversation.status === "open" ? "Ouvert" : conversation.status === "pending" ? "En attente" : "Fermé"}
          </div>

          {isAssignedToMe && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              Assigné à vous
            </span>
          )}

          {!conversation.assigned_agent_id && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
              Non assigné
            </span>
          )}
        </div>
      </header>

      {/* WhatsApp-style Quick Access Bar for Pinned/Starred */}
      {(pinnedMessages.length > 0 || starredMessages.length > 0) && (
        <div className="border-b border-border/50 bg-muted/30 px-4 py-2 flex items-center gap-2 overflow-x-auto">
          {pinnedMessages.length > 0 && (
            <Button
              variant={showPinnedPanel ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs gap-1.5 rounded-full shrink-0"
              onClick={() => {
                setShowPinnedPanel(!showPinnedPanel);
                setShowStarredPanel(false);
              }}
            >
              <Pin size={12} />
              {pinnedMessages.length} épinglé{pinnedMessages.length > 1 ? "s" : ""}
              <ChevronUp size={12} className={`transition-transform ${showPinnedPanel ? "rotate-180" : ""}`} />
            </Button>
          )}
          {starredMessages.length > 0 && (
            <Button
              variant={showStarredPanel ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs gap-1.5 rounded-full shrink-0"
              onClick={() => {
                setShowStarredPanel(!showStarredPanel);
                setShowPinnedPanel(false);
              }}
            >
              <Star size={12} className="fill-current" />
              {starredMessages.length} favoris
              <ChevronUp size={12} className={`transition-transform ${showStarredPanel ? "rotate-180" : ""}`} />
            </Button>
          )}
        </div>
      )}

      {/* Pinned Messages Panel */}
      {showPinnedPanel && pinnedMessages.length > 0 && (
        <div className="border-b border-border/50 bg-primary/5 px-4 py-3 max-h-48 overflow-auto">
          <div className="space-y-2">
            {pinnedMessages.map((msg) => (
              <div key={`pinned-panel-${msg.id}`} className="bg-background rounded-lg px-3 py-2 text-sm flex items-start justify-between gap-2 shadow-sm group">
                <div className="flex-1 min-w-0">
                  <p className="text-foreground line-clamp-2">{msg.content}</p>
                  <span className="text-[10px] text-muted-foreground">
                    {msg.is_from_agent ? "Agent" : "Client"} • {formatTime(msg.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => {
                      scrollToMessage(msg.id);
                      setShowPinnedPanel(false);
                    }}
                  >
                    Aller
                    <ArrowRight size={10} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleTogglePin(msg.id, true)}
                    title="Désépingler"
                  >
                    <PinOff size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Starred Messages Panel */}
      {showStarredPanel && starredMessages.length > 0 && (
        <div className="border-b border-border/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 max-h-48 overflow-auto">
          <div className="space-y-2">
            {starredMessages.map((msg) => (
              <div key={`starred-panel-${msg.id}`} className="bg-background rounded-lg px-3 py-2 text-sm flex items-start justify-between gap-2 shadow-sm group">
                <div className="flex-1 min-w-0">
                  <p className="text-foreground line-clamp-2">{msg.content}</p>
                  <span className="text-[10px] text-muted-foreground">
                    {msg.is_from_agent ? "Agent" : "Client"} • {formatTime(msg.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => {
                      scrollToMessage(msg.id);
                      setShowStarredPanel(false);
                    }}
                  >
                    Aller
                    <ArrowRight size={10} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleToggleStar(msg.id, true)}
                    title="Retirer des favoris"
                  >
                    <Star size={12} className="fill-amber-500 text-amber-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto px-4 py-4 bg-background"
      >
        <div className="space-y-2">
          {messages.map((msg) => (
            <div
              key={msg.id}
              id={`message-${msg.id}`}
              className={`flex ${msg.is_from_agent ? "justify-end" : "justify-start"} group transition-all duration-300`}
            >
              {/* Action buttons on hover (left side for agent messages) */}
              <div className={`flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity self-center ${
                msg.is_from_agent ? "order-first mr-1" : "order-last ml-1"
              }`}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleToggleStar(msg.id, msg.is_starred)}
                  title={msg.is_starred ? "Retirer des favoris" : "Ajouter aux favoris"}
                >
                  <Star size={12} className={msg.is_starred ? "fill-amber-500 text-amber-500" : ""} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleTogglePin(msg.id, msg.is_pinned)}
                  title={msg.is_pinned ? "Désépingler" : "Épingler"}
                >
                  {msg.is_pinned ? <PinOff size={12} /> : <Pin size={12} />}
                </Button>
              </div>

              <div
                className={`max-w-[85%] md:max-w-[70%] px-4 py-2.5 relative ${
                  msg.is_from_agent ? "chat-bubble-user" : "chat-bubble-agent"
                } ${msg.is_pinned ? "ring-1 ring-primary/30" : ""} ${msg.is_starred ? "ring-1 ring-amber-400/30" : ""}`}
              >
                {/* Status icons */}
                <div className="absolute top-1 right-1 flex items-center gap-0.5">
                  {msg.is_pinned && <Pin size={10} className="text-primary" />}
                  {msg.is_starred && <Star size={10} className="fill-amber-500 text-amber-500" />}
                </div>
                
                <TruncatedMessage content={msg.content} maxHeight={50} />
                {msg.attachment_url && msg.attachment_name && msg.attachment_type && (
                  <MessageAttachment
                    url={msg.attachment_url}
                    name={msg.attachment_name}
                    type={msg.attachment_type}
                    isFromAgent={msg.is_from_agent}
                  />
                )}
                <div
                  className={`flex items-center gap-1 mt-1 ${
                    msg.is_from_agent ? "justify-end" : "justify-start"
                  }`}
                >
                  <span
                    className={`text-[10px] ${
                      msg.is_from_agent ? "text-primary-foreground/70" : "text-muted-foreground"
                    }`}
                  >
                    {formatTime(msg.created_at)}
                  </span>
                  {msg.is_from_agent && (
                    <MessageStatus
                      createdAt={msg.created_at}
                      deliveredAt={msg.delivered_at}
                      readAt={msg.read_at}
                      isRead={msg.is_read || false}
                      isFromAgent={msg.is_from_agent}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl px-4 py-2">
                <TypingIndicator typingUsers={typingUsers} />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom button */}
      <ScrollToBottomButton 
        visible={showScrollButton} 
        onClick={() => scrollToBottom()} 
      />

      {/* Input - Disabled if archived */}
      <div className="border-t border-border px-4 py-3 bg-card">
        {isArchived ? (
          <div className="text-center py-2 text-sm text-muted-foreground">
            Cette conversation est archivée. Désarchivez-la pour envoyer des messages.
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <FileUpload
              disabled={sending}
              onFileUploaded={async (url, name, type) => {
                if (!user) return;
                await supabase.from("messages").insert({
                  conversation_id: conversation.id,
                  sender_id: user.id,
                  content: `📎 ${name}`,
                  is_from_agent: true,
                  attachment_url: url,
                  attachment_name: name,
                  attachment_type: type,
                  delivered_at: new Date().toISOString(),
                });
              }}
            />
            <Textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                sendTyping();
              }}
              onKeyDown={handleKeyDown}
              onBlur={stopTyping}
              placeholder="Tapez un message..."
              rows={1}
              className="flex-1 min-h-[44px] max-h-32 resize-none"
            />
            <Button
              variant="gold"
              size="icon"
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="shrink-0 mb-1"
            >
              {sending ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Send size={20} />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Reassign Dialog */}
      <ReassignDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        conversationId={conversation.id}
        currentAgentId={conversation.assigned_agent_id}
      />
    </div>
  );
}
